import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Settings2 } from "lucide-react";
import EntityFormDialog from "@/components/EntityFormDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function ConfigurazioneValutazioni() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/evaluation-questions");
      setQuestions(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = async (q, dir) => {
    const newOrdine = q.ordine + dir;
    try {
      await api.patch(`/evaluation-questions/${q.id}`, { ordine: newOrdine });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (q) => {
    try {
      await api.patch(`/evaluation-questions/${q.id}`, { attiva: !q.attiva });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (q) => {
    if (!window.confirm(`Eliminare la domanda "${q.label}"?`)) return;
    try {
      await api.delete(`/evaluation-questions/${q.id}`);
      toast.success("Domanda eliminata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="config-valutazioni-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5"><Settings2 className="h-3 w-3" /> Configurazione</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Questionario <span className="font-medium">valutazione staff</span></h1>
          <p className="text-sm text-muted-foreground mt-1">Le domande qui configurate verranno proposte ai responsabili quando creano una valutazione.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-question-btn" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Nuova domanda</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ih-glass rounded-2xl p-6">
          <h2 className="font-display text-lg tracking-tight mb-4">Domande</h2>
          {loading ? "Caricamento…" : questions.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna domanda configurata.</p> : (
            <div className="space-y-2">
              {questions.map((q, i) => (
                <div key={q.id} data-testid={`q-row-${q.id}`} className={`rounded-xl border border-border/60 p-3 flex items-center gap-3 ${!q.attiva ? "opacity-50" : ""}`}>
                  <div className="text-xs font-bold w-6 text-muted-foreground">#{q.ordine}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{q.label}</div>
                    {q.categoria && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{q.categoria}</div>}
                  </div>
                  <button onClick={() => move(q, -1)} disabled={i === 0} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center disabled:opacity-30" title="Su"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(q, 1)} disabled={i === questions.length - 1} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center disabled:opacity-30" title="Giù"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => toggle(q)} data-testid={`q-toggle-${q.id}`} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center" title={q.attiva ? "Disattiva" : "Attiva"}>{q.attiva ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
                  <button onClick={() => setEditing(q)} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center" title="Modifica"><Edit3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(q)} data-testid={`q-del-${q.id}`} className="h-8 w-8 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 text-rose-500 flex items-center justify-center" title="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="ih-glass rounded-2xl p-6">
          <h2 className="font-display text-lg tracking-tight mb-4">Anteprima valutatore</h2>
          <p className="text-xs text-muted-foreground mb-4">Così la vedrà chi compila una valutazione (slider 1-10 + commento).</p>
          <div className="space-y-3">
            {questions.filter((q) => q.attiva).map((q) => (
              <div key={q.id} className="rounded-xl border border-border/60 p-4 bg-background/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{q.label}</span>
                  <span className="font-display text-xl text-muted-foreground">7<span className="text-xs">/10</span></span>
                </div>
                <input type="range" min="1" max="10" defaultValue="7" disabled className="w-full accent-lavender-400 opacity-60" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {showAdd && <QuestionFormModal open={showAdd} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <QuestionFormModal open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function QuestionFormModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { label: "", categoria: "operativa", ordine: 99, attiva: true });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
      toast.error("Inserisci un testo per la domanda");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/evaluation-questions/${initial.id}`, form);
        toast.success("Domanda aggiornata");
      } else {
        await api.post("/evaluation-questions", form);
        toast.success("Domanda creata");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? "Modifica domanda" : "Nuova domanda"}
      description="Definisci una domanda del questionario di valutazione staff."
      size="md"
      onSubmit={submit}
      submitLabel={isEdit ? "Salva" : "Crea domanda"}
      saving={saving}
      testId="question-form-modal"
      submitTestId="q-save"
    >
      <div className="space-y-2">
        <Label>Testo della domanda</Label>
        <Input data-testid="q-label" required placeholder="Es. Puntualità e affidabilità" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Categoria</Label>
          <Select value={form.categoria || "__none__"} onValueChange={(v) => setForm({ ...form, categoria: v === "__none__" ? "" : v })}>
            <SelectTrigger data-testid="q-categoria"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="operativa">Operativa</SelectItem>
              <SelectItem value="soft_skills">Soft skills</SelectItem>
              <SelectItem value="__none__">Senza categoria</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Ordine</Label>
          <Input data-testid="q-ordine" type="number" value={form.ordine} onChange={(e) => setForm({ ...form, ordine: parseInt(e.target.value) || 0 })} />
        </div>
      </div>
      <label className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/40 px-3 py-2">
        <span className="text-sm">Domanda attiva</span>
        <Switch checked={!!form.attiva} onCheckedChange={(v) => setForm({ ...form, attiva: v })} />
      </label>
    </EntityFormDialog>
  );
}
